import type { TFunction } from "i18next";
import { ProcedureType, ProcurementOverviewFigure, RegulationBucket } from "../../autogen/bff-api";
import { displayDate } from "../../common/formatting/displayDate";
import {
  LIST_FIGURE_PARAM,
  procurementFigureLabel,
} from "../overview/procurementFigures";
import { regulationLabel } from "../../common/procurement/regulationLabel";

export const OVERVIEW_FILTER_PARAMS = [
  "publishedFrom",
  "publishedTo",
  "awardedFrom",
  "awardedTo",
  "bidDeadlineFrom",
  "bidDeadlineTo",
  "minDeliveredBids",
  "maxDeliveredBids",
  "procedureTypes",
  "regulationBuckets",
  "active",
  "needsSentFrom",
  "needsSentTo",
] as const;

type OverviewFilterParam = (typeof OVERVIEW_FILTER_PARAMS)[number] | "deadline";

const PROCEDURE_TYPES = new Set<string>(Object.values(ProcedureType));
const REGULATION_BUCKETS = new Set<string>(Object.values(RegulationBucket));
const FIGURES = new Set<string>(Object.values(ProcurementOverviewFigure));

export const overviewFilterQuery = (params: URLSearchParams) => {
  const date = (key: OverviewFilterParam) => params.get(key) || undefined;
  const count = (key: OverviewFilterParam) => {
    const value = params.get(key);
    return value != null && /^\d+$/.test(value) ? Number(value) : undefined;
  };
  const list = <T extends string>(key: OverviewFilterParam, known: Set<string>) => {
    const values = (params.get(key) ?? "").split(",").filter((value) => known.has(value)) as T[];
    return values.length ? values : undefined;
  };
  const needsSent = date("needsSentFrom") && date("needsSentTo");
  return {
    needsSentFrom: needsSent ? date("needsSentFrom") : undefined,
    needsSentTo: needsSent ? date("needsSentTo") : undefined,
    publishedFrom: date("publishedFrom"),
    publishedTo: date("publishedTo"),
    awardedFrom: date("awardedFrom"),
    awardedTo: date("awardedTo"),
    bidDeadlineFrom: date("bidDeadlineFrom"),
    bidDeadlineTo: date("bidDeadlineTo"),
    minDeliveredBids: count("minDeliveredBids"),
    maxDeliveredBids: count("maxDeliveredBids"),
    procedureTypes: list<ProcedureType>("procedureTypes", PROCEDURE_TYPES),
    regulationBuckets: list<RegulationBucket>("regulationBuckets", REGULATION_BUCKETS),
    active: params.get("active") === "true" || undefined,
  };
};

export const procurementOverviewFilterChips = (
  t: TFunction,
  params: URLSearchParams,
): { label: string; keys: OverviewFilterParam[] }[] => {
  const query = overviewFilterQuery(params);
  const period = (from?: string, to?: string) => [from, to].map((day) => (day ? displayDate(day) : "…")).join("–");

  return [
    ...(query.needsSentFrom
      ? [
          {
            label: t("Created from needs sent in {{period}}", { period: period(query.needsSentFrom, query.needsSentTo) }),
            keys: ["needsSentFrom", "needsSentTo"] as OverviewFilterParam[],
          },
        ]
      : []),
    ...(query.active ? [{ label: t("Active procurements"), keys: ["active"] as OverviewFilterParam[] }] : []),
    ...(query.publishedFrom || query.publishedTo
      ? [
          {
            label: t("Published {{period}}", { period: period(query.publishedFrom, query.publishedTo) }),
            keys: ["publishedFrom", "publishedTo"] as OverviewFilterParam[],
          },
        ]
      : []),
    ...(query.awardedFrom || query.awardedTo
      ? [
          {
            label: t("Awarded {{period}}", { period: period(query.awardedFrom, query.awardedTo) }),
            keys: ["awardedFrom", "awardedTo"] as OverviewFilterParam[],
          },
        ]
      : []),
    ...(query.bidDeadlineFrom || query.bidDeadlineTo
      ? [
          {
            label: t("Competitions with the bid deadline {{period}}", {
              period: period(query.bidDeadlineFrom, query.bidDeadlineTo),
            }),
            keys: ["bidDeadlineFrom", "bidDeadlineTo", "deadline"] as OverviewFilterParam[],
          },
        ]
      : []),
    ...(query.minDeliveredBids != null || query.maxDeliveredBids != null
      ? [
          {
            label: bidsLabel(t, query.minDeliveredBids, query.maxDeliveredBids),
            keys: ["minDeliveredBids", "maxDeliveredBids"] as OverviewFilterParam[],
          },
        ]
      : []),
  ];
};

export const overviewFigureHeading = (t: TFunction, params: URLSearchParams): string | null => {
  const query = overviewFilterQuery(params);
  const narrowed = procurementOverviewFilterChips(t, params).length > 0 || query.procedureTypes || query.regulationBuckets;
  if (!narrowed) return null;
  const figure = params.get(LIST_FIGURE_PARAM);
  if (figure === "byProcedure") return query.procedureTypes?.length === 1 ? t(query.procedureTypes[0]) : null;
  if (figure === "byRegulation")
    return query.regulationBuckets?.length === 1 ? regulationLabel(t, query.regulationBuckets[0]) : null;
  return figure && FIGURES.has(figure) ? procurementFigureLabel(t, figure as ProcurementOverviewFigure) : null;
};

const bidsLabel = (t: TFunction, min?: number, max?: number): string => {
  if (max === 0) return t("No bids");
  if (min != null && min === max) return t("{{count}} delivered bids", { count: min });
  if (max == null) return t("At least {{count}} delivered bids", { count: min });
  return t("{{min}} to {{max}} delivered bids", { min: min ?? 0, max });
};
